import React, { useContext, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { exchangeCodeForToken, fetchIntercomJWT, getAccessToken } from '../services/Authenticator'
import { isAuthenticated, signOut, me } from '../services'
import {
  segmentIdentify,
  segmentAnonymousId,
  setBugsnagUser,
  googleTagManagerEvent,
  trackSignedIn,
  storageMode
} from '../helpers'
import { growthbook } from '../services/growthbook'
import Intercom from '@intercom/messenger-js-sdk';
import { claimUserCredit } from '../services'

export const UserContext = React.createContext({})
export const useUserContext = () => useContext(UserContext)

export const UserContextProvider = ({ children, initialUser = undefined }) => {
  const { query } = useRouter()
  const { magic } = query
  const localStorageMode = storageMode('localStorage')
  const branchPromoCode = localStorageMode.getItem('branch_promo_code')


  const [user, _setUser] = useState(initialUser)


  // update state and identify to bugsnag & segment
  const setUser = (user) => {
    _setUser(user)

    // identifies a user to segment and bugsnag
    if (user) {
      try {

        segmentIdentify(user.id, { 
          id: user.id,      
          email: user.email, 
          phone: user.phone, 
          fullName: user.fullName,
          firstName: user.nameFirst, 
          lastName: user.nameLast,
          name: user.fullName,
        })
        
        setBugsnagUser(user)
        googleTagManagerEvent('identify', {
          userId: user.id,
          email: user.email,
          segmentAnonymousId: segmentAnonymousId(),
        })
        growthbook.setAttributes({
          id: user.id,
          firstName: user?.nameFirst,
          lastName: user?.nameLast,
          email: user?.email,
          segmentAnonymousId: segmentAnonymousId(),
        })
        trackSignedIn(user.email, user.id)

        fetchIntercomJWT(user.id)
          .then((jwt) => {
            if (!jwt) {
              console.log('no intercom jwt')
              return
            }

            const accessToken = getAccessToken()

            // console.log(JSON.stringify({securityToken}, null, 2))
            // console.log(jwt)            

            return Intercom({
              app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
              hide_default_launcher: true,
              intercom_user_jwt: jwt,

              user_id: user.id,
              name: user.fullName,
              email: user.email,
              created_at: user.createdAt,
              auth_tokens: {
                security_token: accessToken,
              }
            });
          });

      } catch (error) {
        console.warn(error, 'identify user')
      }
    }
  }

  // sign user in if given a magic code
  useEffect(() => {
    if (!magic) {
      return
    }
    exchangeCodeForToken(magic)
      .then(setUser)
  }, [magic])

  // function to reload the user from starbase
  const refreshUser = () => {
    if (!isAuthenticated()) {
      return
    }

    me()
      .then(user => setUser({ ...user }))
      .catch(err => {
        console.error(err)

        // sign out if error fetching user
        if (process.env !== 'development') {
          signOut()
        }
      })
  }

  // load user if we're authenticated
  useEffect(() => {
    refreshUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user) {
      return
    }

    growthbook.setAttributes({
      segmentAnonymousId: segmentAnonymousId(),
    })

  }, [user])

  useEffect(() => {
    if (user && branchPromoCode) {
      claimUserCredit(user.id, branchPromoCode)
      localStorageMode.removeItem('branch_promo_code')
    }
  }, [user, branchPromoCode, localStorageMode])

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser }}>
      {children}
    </UserContext.Provider>
  )
}
